import { Slot }  from "./Slot";

import Tooltip from '@material-ui/core/Tooltip';

import { Currency } from 'react-frontend-utils';
import { FormTemplate } from 'form-builder'
import { ThemeColors } from '../Theme';
import { DateConversions } from '../utils/DateConversions';
import { Global } from './Global';

export class Patron {

    static Status = {
        MANUALLY_CONFIRMED:    {enumName: "MANUALLY_CONFIRMED", displayName: "Manually Added", color: 'green'},
        CONFIRMED_FREE:        {enumName: "CONFIRMED_FREE", displayName: "Confirmed", color: 'green'},
        CONFIRMED_OTHER:       {enumName: "CONFIRMED_OTHER", displayName: "Confirmed", color: 'green'},
        CONFIRMED_PAID:        {enumName: "CONFIRMED_PAID", displayName: "Confirmed", color: 'green'},
        PAYMENT_PENDING:       {enumName: "PAYMENT_PENDING", displayName: "Reserved", color: 'orange'},
        CANCELLED:             {enumName: "CANCELLED", displayName: "Cancelled", color: 'red'}
    };

    id;
    patronCount;
    patronNames;
    patronEmail;
    patronPhone;
    enableSms;
    patronQuestions;
    optionalCosts;
    optionalCostsQuantities;
    calculatedCost;
    isoCurrency;

    patronBooked;
    paymentIntent;
    paidOn;
    refundedAmount;
    isCashPayment;
    cashPaymentPaid;

    membershipInfo;

    checkedInCount;   // only used for Patron Check-In on Events, the number of Patrons checked in (max = patronCount)

    cancelled;      // only used for Event-Signup, not Bookings. For Bookings, the booking is cancelled

    constructor(json) {

        if (json) {
            this.id = json.id;  
            this.patronCount = json.patronCount;
            this.patronNames = json.patronNames;
            this.patronEmail = json.patronEmail;
            this.patronPhone = json.patronPhone;
            this.contactName = json.contactName;
            this.enableSms = json.enableSms;
            this.optionalCosts = json.optionalCosts;
            this.optionalCostsQuantities = json.optionalCostsQuantities;
            this.calculatedCost = json.calculatedCost;
            this.isoCurrency = json.isoCurrency;
            this.patronBooked = json.patronBooked;      // do not convert to a Luxon DateTime here so we don't have to modify before posting back to server
            this.paymentIntent = json.paymentIntent;
            this.paidOn = json.paidOn;
            this.refundedAmount = json.refundedAmount;
            this.isCashPayment = json.isCashPayment;
            this.cashPaymentPaid = json.cashPaymentPaid;

            this.membershipInfo = json.membershipInfo;
            this.checkedInCount = json.checkedInCount ? json.checkedInCount : 0;
            this.cancelled = json.cancelled;

            if (json.patronQuestions) {  //Field tree only populated sometimes
                this.patronQuestions = json.patronQuestions;
                if (this.patronQuestions.fieldTree)
                    this.patronQuestions.fieldTree.isBase = true;           //mark this as the base of the tree
            } 
        }
        else 
            this.id = "pt_" + Slot.randomId();  // new Patron

    }

    copy() {
        const copy = new Patron();
        copy.id = this.id;
        copy.patronCount = this.patronCount;
        copy.patronNames = this.patronNames;
        copy.patronEmail = this.patronEmail;
        copy.patronPhone = this.patronPhone;
        copy.contactName = this.contactName;
        copy.enableSms = this.enableSms;
        copy.optionalCosts = this.optionalCosts;
        copy.optionalCostsQuantities = this.optionalCostsQuantities;
        copy.calculatedCost = this.calculatedCost;
        copy.isoCurrency = this.isoCurrency;
        copy.patronBooked = this.patronBooked;
        copy.paymentIntent = this.paymentIntent;
        copy.paidOn = this.paidOn;
        copy.refundedAmount = this.refundedAmount;
        copy.isCashPayment = this.isCashPayment;
        copy.cashPaymentPaid = this.cashPaymentPaid;
        copy.membershipInfo = this.membershipInfo;
        copy.checkedInCount = this.checkedInCount ? this.checkedInCount : 0;
        copy.cancelled = this.cancelled;

        if (this.patronQuestions) {
            copy.patronQuestions = {type: "PatronQuestions"};
            copy.patronQuestions.fieldTree = FormTemplate.copyField(this.patronQuestions.fieldTree);
        }
        return copy;
    }

    clear() {
        this.patronCount = 0;
        this.patronNames = [];
        this.patronEmail = null;
        this.patronPhone = null;
        this.contactName = null;
        this.enableSms = false;
        this.patronQuestions = null;
        this.optionalCosts = [];
        this.calculatedCost = 0;
        this.isoCurrency = null;
        this.patronBooked = null;
        this.paymentIntent = null;
        this.paidOn = null;
        this.refundedAmount = null;
        this.isCashPayment = false;
        this.cashPaymentPaid = false;
        this.membershipInfo = null;
        this.checkedInCount = 0;
        this.cancelled = false;
    }


    status() {

        if (this.cancelled)
            return Patron.Status.CANCELLED;     // only for Event Signup!

        if (!this.patronBooked)
            return Patron.Status.MANUALLY_CONFIRMED;
        if (this.calculatedCost === 0)
            return Patron.Status.CONFIRMED_FREE;
        if (this.isCashPayment)
            return Patron.Status.CONFIRMED_OTHER;
        if (this.paymentIntent && this.paidOn)
            return Patron.Status.CONFIRMED_PAID;

        return Patron.Status.PAYMENT_PENDING;
    }

    hasPatronQuestions() {
        return this.patronQuestions && this.patronQuestions.fieldTree && this.patronQuestions.fieldTree.children.length > 0;
    }

    refundable() {
        return this.paymentIntent && this.calculatedCost > 0 && this.paidOn && this.refundedAmount < this.calculatedCost;
    }

    isManual() {
        return !this.patronBooked;
    }

    // Get the luxon DateTime when the Patron booked, or null if the patron did not book (i.e. was manually added)
    patronBookDate() {
        return this.patronBooked ? DateConversions.utcJsonDateStringToLuxonDateTime(this.patronBooked).setZone(Global.getTimezone()) : null;
    }

    getPrimaryPatron() {
        return this.patronNames[0];
    }

    getCommaSeparatedNames() {

        let names = "";
        if (this.patronNames)
            names = this.patronNames.filter(name => name && name.trim()).join(", ");

        // Get a count of the blank names
        let blankCount = 0;
        if (this.patronNames)
            blankCount = this.patronNames.filter(name => !name || !name.trim()).length;

        const blankText = blankCount > 0 ? " (+" + blankCount + " unnamed)" : "";

        return {names, blankText};
    }


    getAdditionalPurchases() {
        let additional;
        if (this.optionalCosts) {
            const optStrings = this.optionalCosts.map((cost, index) => {
                const quantity = this.optionalCostsQuantities ? this.optionalCostsQuantities[index] : 1;
                return cost + (quantity > 1 ? " (x" + quantity + ")" : "");
            });
            additional = optStrings.join(", ");
        }
        return additional;
    }

    canCheckInMore() {
        return this.checkedInCount < this.patronCount;
    }

    canRemoveCheckIn() {
        return this.checkedInCount > 0;
    }


    details() {
        // Get a comma seperated list of Patrons, ignoring blank names or those with only whitespace
        const {names, blankText} = this.getCommaSeparatedNames();

        // Additional purchases:
        const additional = this.getAdditionalPurchases();

        return (
            "<div>" +
                (this.contactName ? ("<div>Contact Name: " + this.contactName + "</div>") : "") +
                (this.patronEmail ? ("<div>Contact Email: " + this.patronEmail + "</div>") : "") +
                (this.patronPhone ? ("<div>Contact Phone: " + this.patronPhone + (this.enableSms ? " (SMS)" : "") + "</div>") : "") +
                (names ? ("<div>Patrons: " + names + blankText + "</div>") : "") +
                (this.membershipInfo ? ("<div>Membership: " + this.membershipInfo + "</div>") : "") +
                (additional ? ("<div>Additional Purchases: " + additional + "</div>") : "") +
            "</div>"
        );
    }

    paymentBadge() {
        let bookingPaid;
        let tooltip;


        const calculatedCost = Number(Currency.round(this.calculatedCost));
        const refundedAmount = this.refundedAmount > 0 ? Number(Currency.round(this.refundedAmount)) : 0;


        if (this.isCashPayment || (this.paymentIntent && this.paidOn)) {        // cash payment or paid
            if (this.isCashPayment) {
                if (this.cashPaymentPaid) {
                    bookingPaid = {background: 'green', textColor: 'white', text: 'PAID (OTHER)'};
                    tooltip = "Paid: " +  calculatedCost + " " + this.isoCurrency + " using other payment method";
                }
                else {
                    bookingPaid = {background: 'yellow', textColor: 'black', text: 'OTHER PAYMENT DUE'};
                    tooltip = "Due from Patron: " +  calculatedCost + " " + this.isoCurrency;
                }
            }
            else if (refundedAmount > 0) {
                bookingPaid = {background: 'cyan', textColor: 'black', text: refundedAmount === calculatedCost ? 'REFUNDED' : 'PARTIALLY REFUNDED'};
                tooltip = "Paid: " + calculatedCost + ", Refunded: " + refundedAmount + " " + this.isoCurrency;
            }
            else {
                bookingPaid = {background: 'green', textColor: 'white', text: 'PAID'};
                tooltip = "Paid: " + calculatedCost + " " + this.isoCurrency
            }   
        
            return <Tooltip title={tooltip}>
                        <div style={{backgroundColor: bookingPaid.background, color: bookingPaid.textColor, padding: 2, borderRadius: 2, textAlign: 'center'}}>{bookingPaid.text}</div>
                    </Tooltip>;
        }
        else if (this.status() === Patron.Status.PAYMENT_PENDING) {
            tooltip = "Amount Due: " + calculatedCost + " " + this.isoCurrency;
            return <Tooltip title={tooltip}>
                        <div style={{backgroundColor:'orange', color: 'white', padding: 2, borderRadius: 2, textAlign: 'center'}}>PAYMENT IN PROCESS</div>
                    </Tooltip>;
        }
        else if (this.status() === Patron.Status.CONFIRMED_FREE) {
            return <div style={{backgroundColor: ThemeColors.freeGreen, color: 'black', padding: 2, borderRadius: 2, textAlign: 'center'}}>FREE</div>
        }
    }


    //This function must return an array of available Patron Question fields, each item in the array having a label, icon, and a callback function.  The callback function should
    //(a) create one or more new fields and (b) call the provided insertFunction to insert the array of new fields into the field tree 
    //baseField is assumed to be the root of the field tree
    static getAvailableFields(baseField, insertFunc, currency) {

        const createNumericField = () => {
            const field = FormTemplate.createNewField(baseField, "NumericField");
            field.currency = currency
            console.log(field)
            return field;
        }
          
        return [{label: "Text Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "TextField")] )},   //array of one field
                {label: "Address Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "AddressField")] )},   //array of one field
                {label: "Email Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "EmailField")] )},   //array of one field
                {label: "Numeric Field", icon: null, selectCallback: () => insertFunc( [createNumericField()] )},   //array of one field
                {label: "Date Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "DateField")] )},   //array of one field
                {label: "Select Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "SelectField")] )},   //array of one field
                {label: "Checkbox Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "CheckboxField")] )},   //array of one field
                {label: "Image Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "ImageField")] )},   //array of one field
                {label: "Signature Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "SignatureField")] )},   //array of one field
                {label: "Rich Text Field", icon: null, selectCallback: () => insertFunc( [FormTemplate.createNewField(baseField, "RichTextHtmlField")] )},   //array of one field
                ];
    }

}
